import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Heart, Sparkles, Shield, Swords, Sword, Loader2, Trophy, Skull, ChevronUp, Zap, Flame, Users, Crown, Backpack, X, User, Ghost, Footprints } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import soundManager from "@/utils/SoundManager";
import ambientMusic from "@/utils/AmbientMusic";
import GameCard from "./GameCard";
import BattleHand from "./BattleHand";
import BattleAlliedField from "./BattleAlliedField";
import useCardFlight from "@/hooks/useCardFlight";
import { t, tf } from "@/utils/i18n";
import { resolveImageUrl } from "@/utils/imageUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMBO_ICONS = {
  swords: Swords, shield: Shield, heart: Heart, sparkles: Sparkles, users: Users, flame: Flame
};

// Map basic card IDs to i18n keys
const BASIC_CARD_I18N = {
  basic_strike: { name: "card_strike", desc: "card_strike_desc" },
  basic_block: { name: "card_block", desc: "card_block_desc" },
  basic_heal: { name: "card_heal", desc: "card_heal_desc" },
  basic_power_strike: { name: "card_power_strike", desc: "card_power_strike_desc" },
};

// Map combo IDs to i18n keys
const COMBO_I18N = {
  double_strike: { name: "combo_double_strike", desc: "combo_double_strike_desc" },
  fortified_assault: { name: "combo_fortified", desc: "combo_fortified_desc" },
  healing_surge: { name: "combo_healing", desc: "combo_healing_desc" },
  arcane_barrage: { name: "combo_arcane", desc: "combo_arcane_desc" },
  guardians_bond: { name: "combo_guardian", desc: "combo_guardian_desc" },
  berserker_rage: { name: "combo_berserker", desc: "combo_berserker_desc" },
};

function translateCard(card, lang) {
  if (card.is_basic && BASIC_CARD_I18N[card.id]) {
    return {
      ...card,
      name: t(lang, BASIC_CARD_I18N[card.id].name),
      description: t(lang, BASIC_CARD_I18N[card.id].desc),
    };
  }
  return card;
}

function translateCombo(combo, lang) {
  const comboKey = combo.id || combo.name?.toLowerCase().replace(/['\s]+/g, '_');
  const mapping = COMBO_I18N[comboKey];
  if (mapping) {
    return { ...combo, name: t(lang, mapping.name), description: t(lang, mapping.desc) };
  }
  return combo;
}

function ComboNotification({ combo, onDone }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 2500);
    return () => clearTimeout(timer);
  }, [onDone]);

  const Icon = COMBO_ICONS[combo.icon] || Sparkles;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="combo-notification">
      <div className="animate-combo-flash text-center">
        <div className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-[#0a0a0c]/90 border-2 backdrop-blur-md" style={{ borderColor: combo.color }}>
          <Icon size={28} style={{ color: combo.color }} />
          <div>
            <p className="font-heading text-xl font-bold" style={{ color: combo.color }}>{combo.name}</p>
            <p className="text-sm text-[#94a3b8]">{combo.description}</p>
            <p className="text-xs font-heading mt-1" style={{ color: combo.color }}>
              +{combo.bonus_value} {combo.bonus_type === "bonus_damage" ? "DMG" : combo.bonus_type === "bonus_heal" ? "HEAL" : "BUFF"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BattleArena() {
  const { game, battleAction, endBattle, loadGame, loading } = useGame();
  const { token, API } = useAuth();
  const [acting, setActing] = useState(false);
  const [battleLog, setBattleLog] = useState([]);
  // Iter 106 — heal target picker (card id whose use needs target selection)
  const [healPickerCardId, setHealPickerCardId] = useState(null);
  const [activeCombo, setActiveCombo] = useState(null);
  const [cardsPlayedCount, setCardsPlayedCount] = useState(0);
  const [overlayAnim, setOverlayAnim] = useState(null); // "slash-left" | "slash-right" | "heal" | "shield"
  const [enemyHit, setEnemyHit] = useState(false);
  const [playerHit, setPlayerHit] = useState(false);
  const [alliesHit, setAlliesHit] = useState(false);
  const [lungeAnim, setLungeAnim] = useState(null); // "allies" | "enemy"

  const triggerOverlayAnim = useCallback((type, duration = 600) => {
    setOverlayAnim(type);
    setTimeout(() => setOverlayAnim(null), duration);
  }, []);

  const triggerEnemyHit = useCallback((duration = 500) => {
    setEnemyHit(true);
    setTimeout(() => setEnemyHit(false), duration);
  }, []);

  const triggerPlayerHit = useCallback((duration = 500) => {
    setPlayerHit(true);
    setTimeout(() => setPlayerHit(false), duration);
  }, []);

  const triggerAlliesHit = useCallback((duration = 500) => {
    setAlliesHit(true);
    setTimeout(() => setAlliesHit(false), duration);
  }, []);

  const triggerLunge = useCallback((type, duration = 500) => {
    setLungeAnim(type);
    setTimeout(() => setLungeAnim(null), duration);
  }, []);
  const [fleeingActive, setFleeingActive] = useState(false);
  const [bossLoot, setBossLoot] = useState([]);
  const [showBattleInv, setShowBattleInv] = useState(false);
  // iter 77 — manual ally targeting per ally
  const [allyTargets, setAllyTargets] = useState({});
  const handleAllyPickTarget = useCallback((allyId, targetKey) => {
    setAllyTargets((cur) => ({ ...cur, [allyId]: targetKey }));
  }, []);

  // iter 76 — card flight animation: ref registry + projectile overlay
  const { registerRef, flyCard, FlyOverlay } = useCardFlight();

  const lang = game?.language || "en";
  const battle = game?.battle_state;
  const character = game?.character;

  useEffect(() => {
    if (battle?.active) {
      soundManager.playEnemyAppear();
    }
  }, []);

  const showCombo = (combo) => {
    soundManager.playCombo();
    setActiveCombo(combo);
  };

  const handlePlayCard = useCallback(async (cardId, healTarget) => {
    if (!game?.id || acting) return;
    setActing(true);
    soundManager.playCardPlay();
    try {
      const card = battle?.player_hand?.find(c => c.id === cardId);
      // Iter 106 — for heal cards with at least one ally on the field,
      // ask the player WHO to heal unless healTarget already provided.
      if (card?.effect === "heal" && !healTarget && (battle?.player_field?.length || 0) > 0) {
        setActing(false);
        setHealPickerCardId(cardId);
        return;
      }
      // iter 76: card flight animation toward the appropriate target zone
      if (card) {
        const targetKey = card.type === "ally" ? "battlefield-zone"
          : (card.effect === "damage" || card.effect === "debuff" || card.effect === "stun") ? "enemy-portrait"
          : (card.effect === "heal" && healTarget && healTarget !== "player") ? `ally-${healTarget}`
          : "player-portrait";
        try { flyCard(cardId, targetKey, card); } catch { /* non-fatal */ }
      }
      // Trigger animation based on card type
      if (card?.effect === "damage") triggerOverlayAnim("slash-left", 600);
      else if (card?.effect === "heal") triggerOverlayAnim("heal", 800);
      else if (card?.effect === "defense") triggerOverlayAnim("shield", 700);

      // Tiny delay so the flight visually completes before the state mutation
      // collapses the card out of the hand. 280ms ≈ mid-flight, before the
      // "impact" phase swaps in.
      await new Promise((res) => setTimeout(res, 280));

      const result = await battleAction(game.id, "play_card", cardId, null, healTarget);
      setBattleLog(prev => [...prev, ...(result.player_log || [])]);
      setCardsPlayedCount(c => c + 1);

      if (card?.effect === "damage") {
        soundManager.playAttack();
        triggerEnemyHit(500);
      }
      else if (card?.effect === "heal") soundManager.playHeal();
      else if (card?.effect === "defense") soundManager.playDefend();

      if (result.battle_ended) {
        if (result.boss_loot?.length > 0) {
          setBossLoot(result.boss_loot);
          toast.success(t(lang, 'boss_defeated_msg'));
        } else {
          toast.success(t(lang, 'victory'));
        }
        if (result.replay_loot) {
          const isIt = lang.startsWith("it");
          if (result.replay_loot.dust > 0) {
            toast.success(isIt
              ? `+${result.replay_loot.dust} polvere arcana`
              : `+${result.replay_loot.dust} arcane dust`);
          }
          if (result.replay_loot.ally_card) {
            toast.success(isIt
              ? `Nuovo alleato: ${result.replay_loot.ally_card.name}!`
              : `New ally: ${result.replay_loot.ally_card.name}!`);
            soundManager.playNewItem();
          }
        }
        // Bond level milestones (ally cards leveled up)
        if (result.bond_milestones?.length > 0) {
          const isIt = lang.startsWith("it");
          result.bond_milestones.forEach((m) => {
            const bonusLabel = m.threshold === 3
              ? (isIt ? "+1 ATT" : "+1 ATK")
              : m.threshold === 7
                ? (isIt ? "+5 HP" : "+5 HP")
                : (isIt ? "FOIL ✦" : "FOIL ✦");
            toast.success(isIt
              ? `Legame ${m.threshold}: ${m.name} ottiene ${bonusLabel}`
              : `Bond ${m.threshold}: ${m.name} earned ${bonusLabel}`);
            soundManager.playNewItem();
          });
        }
        soundManager.playVictory();
        ambientMusic.play('victory');
      }
      if (result.player_dead) {
        toast.error(t(lang, 'defeated'));
        soundManager.playDefeat();
        ambientMusic.play('defeat');
      }
      if (result.new_achievements?.length > 0) {
        result.new_achievements.forEach(() => toast.success(t(lang, 'achievement_unlocked')));
      }
    } catch (e) {
      toast.error("Action failed");
    } finally {
      setActing(false);
    }
  }, [game?.id, battleAction, acting, battle, lang]);

  const handleEndTurn = useCallback(async () => {
    if (!game?.id || acting) return;
    setActing(true);
    soundManager.playClick();
    try {
      const result = await battleAction(game.id, "end_turn", null, allyTargets);
      setBattleLog(prev => [...prev, ...(result.player_log || []), ...(result.enemy_log || [])]);
      setCardsPlayedCount(0);
      // iter 77 — clear ally target choices each turn (defaults back to "enemy")
      setAllyTargets({});

      if (result.triggered_combos && result.triggered_combos.length > 0) {
        for (let i = 0; i < result.triggered_combos.length; i++) {
          setTimeout(() => showCombo(translateCombo(result.triggered_combos[i], lang)), i * 800);
        }
      }

      // Animate card movements
      const hasAllies = (battle?.player_field || []).length > 0;
      if (hasAllies) {
        // Trigger allies attacking first
        triggerLunge("allies", 500);
        // Play attack sound and hit animation on enemy when allies hit (around 200ms into the lunge)
        setTimeout(() => {
          soundManager.playAttack();
          triggerOverlayAnim("slash-left", 600);
          triggerEnemyHit(500);
        }, 200);

        // After allies attack completes, trigger enemy attack
        if (result.enemy_log?.length > 0) {
          setTimeout(() => {
            soundManager.playAttack();
            triggerLunge("enemy", 500);
            setTimeout(() => {
              triggerOverlayAnim("slash-right", 600);
              triggerPlayerHit(500);
              triggerAlliesHit(500);
            }, 150);
          }, 750);
        }
      } else {
        // No allies: enemy attacks immediately if they acted
        if (result.enemy_log?.length > 0) {
          soundManager.playAttack();
          triggerLunge("enemy", 500);
          setTimeout(() => {
            triggerOverlayAnim("slash-right", 600);
            triggerPlayerHit(500);
          }, 150);
        }
      }
      if (result.battle_ended) {
        if (result.boss_loot?.length > 0) {
          setBossLoot(result.boss_loot);
          toast.success(t(lang, 'boss_defeated_msg'));
        } else {
          toast.success(t(lang, 'victory'));
        }
        if (result.replay_loot) {
          const isIt = lang.startsWith("it");
          if (result.replay_loot.dust > 0) {
            toast.success(isIt
              ? `+${result.replay_loot.dust} polvere arcana`
              : `+${result.replay_loot.dust} arcane dust`);
          }
          if (result.replay_loot.ally_card) {
            toast.success(isIt
              ? `Nuovo alleato: ${result.replay_loot.ally_card.name}!`
              : `New ally: ${result.replay_loot.ally_card.name}!`);
            soundManager.playNewItem();
          }
        }
        // Bond level milestones (ally cards leveled up)
        if (result.bond_milestones?.length > 0) {
          const isIt = lang.startsWith("it");
          result.bond_milestones.forEach((m) => {
            const bonusLabel = m.threshold === 3
              ? (isIt ? "+1 ATT" : "+1 ATK")
              : m.threshold === 7
                ? (isIt ? "+5 HP" : "+5 HP")
                : (isIt ? "FOIL ✦" : "FOIL ✦");
            toast.success(isIt
              ? `Legame ${m.threshold}: ${m.name} ottiene ${bonusLabel}`
              : `Bond ${m.threshold}: ${m.name} earned ${bonusLabel}`);
            soundManager.playNewItem();
          });
        }
        soundManager.playVictory();
        ambientMusic.play('victory');
      }
      if (result.player_dead) { toast.error(t(lang, 'defeated')); soundManager.playDefeat(); ambientMusic.play('defeat'); }
    } catch (e) {
      toast.error("End turn failed");
    } finally {
      setActing(false);
    }
  }, [game?.id, battleAction, acting, lang, allyTargets, battle, triggerLunge]);

  const [fleeConfirm, setFleeConfirm] = useState(false);
  const handleFleeActive = useCallback(async () => {
    if (!game?.id || fleeingActive || acting) return;
    if (!fleeConfirm) {
      setFleeConfirm(true);
      setTimeout(() => setFleeConfirm(false), 4000);
      return;
    }
    setFleeConfirm(false);
    setFleeingActive(true);
    soundManager.playClick();
    try {
      const res = await axios.post(
        `${API}/game/${game.id}/battle/flee`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status === "cannot_flee") {
        toast.error(res.data.reason || (lang.startsWith("it") ? "Non puoi fuggire" : "Cannot flee"));
      } else {
        toast.success(lang.startsWith("it") ? "Fuga riuscita" : "You escape");
        ambientMusic.play('exploration');
      }
      await loadGame(game.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Flee failed");
    } finally {
      setFleeingActive(false);
    }
  }, [game?.id, token, API, acting, fleeingActive, fleeConfirm, lang, loadGame]);

  const handleContinueStory = async () => {
    try {
      await endBattle(game.id);
      toast.success(t(lang, 'continue_adventure'));
      ambientMusic.play('exploration');
    } catch {
      toast.error("Failed to continue");
    }
  };

  const handleItemUsed = (result) => {
    if (result.result_msg) {
      setBattleLog(prev => [...prev, `Item: ${result.result_msg}`]);
    }
    if (result.battle_ended) {
      toast.success(t(lang, 'victory'));
      soundManager.playVictory();
      ambientMusic.play('victory');
    }
    setShowBattleInv(false);
  };

  // Battle log classification + auto-scroll (hooks must run before any early return)
  const logEndRef = useRef(null);
  const classifiedLog = useMemo(() => {
    const lines = battle?.log || [];
    const enemyName = (battle?.enemy?.name || "").toLowerCase();
    return lines.map((msg) => {
      const text = String(msg || "");
      const lower = text.toLowerCase();
      let kind = "system";
      // COMBO (already-styled marker)
      if (text.startsWith("COMBO:") || lower.includes("combo")) kind = "combo";
      // Enemy-originated lines
      else if (
        lower.startsWith("enemy ") ||
        (enemyName && lower.startsWith(enemyName + " ")) ||
        (enemyName && lower.includes(`${enemyName} attacks`)) ||
        lower.includes(" raises its defenses") ||
        lower.includes("uses a special attack") ||
        (enemyName && lower.includes(`${enemyName} uses`))
      ) kind = "enemy";
      // Player actions (cards played, allies)
      else if (
        lower.startsWith("used ") ||
        lower.includes("joins the battle") ||
        lower.includes("enters the battle") ||
        lower.includes("gained ") && lower.includes(" armor") ||
        lower.includes("restored ") && lower.includes(" hp")
      ) kind = "player";
      // Ally attack vs enemy: look for pattern "X attacks {enemyName}"
      else if (enemyName && lower.includes(` attacks ${enemyName}`)) kind = "player";
      // Outcome / destroyed
      else if (lower.includes("has been defeated") || lower.includes("has been destroyed") || lower.includes("escape with your life") || lower.includes("boss defeated") || lower.includes("battle begins")) kind = "outcome";
      // Rewards / level up
      else if (lower.includes("level up") || lower.includes("gold") || lower.includes("stat point") || lower.includes("evolution")) kind = "reward";
      return { text, kind };
    });
  }, [battle?.log, battle?.enemy?.name]);

  useEffect(() => {
    // Auto-scroll the battle log to bottom when new entries arrive
    if (logEndRef.current) {
      try { logEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" }); } catch { /* ignore */ }
    }
  }, [classifiedLog.length]);

  // Defensive guards: if `battle` or its nested fields are missing, a direct
  // access to `battle.enemy.is_boss` below would throw and unmount the whole
  // component, leaving the user with a black screen. If this happens mid-action
  // (transient inconsistency between action result and context state) we show a
  // recovery UI with a "Reload" button instead of crashing.
  if (!battle || !battle.enemy || !character) {
    return (
      <div className="min-h-[100dvh] battle-arena flex items-center justify-center p-6" data-testid="battle-arena-recovering">
        <div className="text-center max-w-sm">
          <Loader2 size={40} className="mx-auto mb-4 text-[#cfb53b] animate-spin" />
          <p className="font-heading text-[#cfb53b] mb-2">
            {lang.startsWith("it") ? "Sincronizzazione battaglia..." : "Syncing battle..."}
          </p>
          <p className="text-xs text-[#94a3b8] mb-4">
            {lang.startsWith("it")
              ? "Se lo schermo resta fermo, tocca Ricarica."
              : "If the screen stays stuck, tap Reload."}
          </p>
          <Button
            data-testid="battle-recover-reload"
            onClick={() => { try { loadGame(game?.id); } catch { /* noop */ } }}
            className="bg-[#cfb53b] hover:bg-[#d4af37] text-black"
          >
            {lang.startsWith("it") ? "Ricarica" : "Reload"}
          </Button>
        </div>
      </div>
    );
  }

  const enemy = battle.enemy;
  const isBoss = enemy.is_boss;
  const enemyHpPercent = Math.max(0, (enemy.hp / enemy.max_hp) * 100);
  const playerHpPercent = Math.max(0, (character.hp / character.max_hp) * 100);
  const playerHpColor = playerHpPercent > 60 ? "high" : playerHpPercent > 30 ? "medium" : "";
  const enemyHpColor = enemyHpPercent > 60 ? "high" : enemyHpPercent > 30 ? "medium" : "";
  const isPlayerTurn = battle.phase === "player";

  if (!battle.active) {
    const won = enemy.hp <= 0;
    const isBoss = enemy.is_boss;
    return (
      <div className="min-h-screen battle-arena flex items-center justify-center" data-testid="battle-ended">
        <div className="text-center max-w-md mx-auto px-4 animate-fadeInUp">
          {won ? (
            <>
              {isBoss ? (
                <Crown size={72} className="mx-auto text-[#cfb53b] mb-4 drop-shadow-[0_0_20px_rgba(207,181,59,0.5)]" />
              ) : (
                <Trophy size={72} className="mx-auto text-[#cfb53b] mb-4" />
              )}
              <h2 className="font-heading text-4xl text-[#cfb53b] mb-3">
                {isBoss ? t(lang, 'boss_battle') + "!" : t(lang, 'victory')}
              </h2>
              <p className="text-[#94a3b8] mb-2 text-lg">{enemy.name} {t(lang, 'enemy_defeated')}</p>
              <p className="text-sm text-[#cfb53b]">+{enemy.max_hp * (isBoss ? 2.5 : 1)} {t(lang, 'xp_earned')}</p>
              
              {/* Boss Loot Display */}
              {bossLoot.length > 0 && (
                <div className="mt-6 bg-[#1a1a1d]/80 border border-[#cfb53b]/40 rounded-lg p-4" data-testid="boss-loot-panel">
                  <h3 className="font-heading text-lg text-[#cfb53b] mb-3 flex items-center justify-center gap-2">
                    <Crown size={18} /> {t(lang, 'boss_loot_title')}
                  </h3>
                  <div className="space-y-2">
                    {bossLoot.map((item, i) => (
                      <div key={item.id || item.name || `loot-${i}`} className={`p-3 rounded-lg border ${item.rarity === 'epic' ? 'border-purple-500/50 bg-purple-500/10' : 'border-[#cfb53b]/50 bg-[#cfb53b]/10'}`} data-testid={`boss-loot-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[10px] font-heading uppercase px-2 py-0.5 rounded-full ${item.rarity === 'epic' ? 'bg-purple-500/30 text-purple-300' : 'bg-[#cfb53b]/30 text-[#cfb53b]'}`}>
                            {item.rarity === 'epic' ? t(lang, 'epic') : t(lang, 'rare')}
                          </span>
                          <span className="font-heading text-sm text-[#e2e8f0]">{item.name}</span>
                        </div>
                        <p className="text-xs text-[#94a3b8]">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isBoss && (
                <p className="text-sm text-[#cfb53b]/70 mt-3 font-heading">{t(lang, 'chapter_complete')}</p>
              )}
            </>
          ) : (
            <>
              <Skull size={72} className="mx-auto text-[#8b0000] mb-4" />
              <h2 className="font-heading text-4xl text-[#8b0000] mb-3">{t(lang, 'defeated')}</h2>
              <p className="text-[#94a3b8] text-lg">{t(lang, 'escape_alive')}</p>
            </>
          )}
          <Button
            data-testid="continue-story-button"
            onClick={handleContinueStory}
            className="mt-8 bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading font-bold px-10 py-3 text-lg"
          >
            {t(lang, 'continue_adventure')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen battle-arena" data-testid="battle-arena">
      {activeCombo && <ComboNotification combo={activeCombo} onDone={() => setActiveCombo(null)} />}

      <div className="max-w-5xl lg:max-w-6xl xl:max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-4">
        {/* Turn & Status Bar */}
        <div className="text-center mb-3">
          {isBoss && (
            <div className="mb-1 flex items-center justify-center gap-1.5" data-testid="boss-indicator">
              <Crown size={14} className="text-[#cfb53b]" />
              <span className="font-heading text-xs sm:text-sm text-[#cfb53b] tracking-wider animate-pulse">{t(lang, 'boss_battle')}</span>
              <Crown size={14} className="text-[#cfb53b]" />
            </div>
          )}
          <div className={`inline-flex items-center gap-2 ${isBoss ? 'bg-[#cfb53b]/10 border-[#cfb53b]/40' : 'bg-[#1a1a1d]/80 border-[#cfb53b]/20'} border rounded-full px-4 py-1`}>
            <span className="font-heading text-xs text-[#cfb53b]">{t(lang, 'turn')} {battle.turn}</span>
            <span className="w-1 h-1 rounded-full bg-[#cfb53b]/30" />
            <span className={`font-heading text-xs ${isPlayerTurn ? 'text-[#10b981]' : 'text-[#dc2626]'}`}>
              {isPlayerTurn ? t(lang, 'your_turn') : t(lang, 'enemy_turn')}
            </span>
          </div>
        </div>

        {/* 3D STONE BATTLEFIELD BOARD */}
        <div className="battle-arena-board relative shadow-2xl flex flex-col justify-between p-4 min-h-[440px] md:min-h-[480px] lg:min-h-[580px] xl:min-h-[660px]">


          {/* 1. TOP ZONE (OPPONENT BOARD & HUD) */}
          <div className="flex flex-col gap-2 w-full z-10">
            {/* Opponent HUD Area */}
            <div className="flex items-center justify-between px-2 opacity-90 scale-90 sm:scale-100 origin-left">
              {/* Opponent Deck Pile */}
              <div className="ccg-deck-pile flex items-center justify-center border-2 border-red-900/50" title={lang.startsWith("it") ? "Mazzo Avversario" : "Opponent Deck"}>
                <span className="absolute -top-2.5 bg-black/80 px-1 rounded text-[8px] border border-red-950 text-red-400">OPP_DECK</span>
                <span className="font-bold text-xs text-red-400">{(battle.enemy_deck || []).length || 5}</span>
              </div>

              {/* Opponent Cards in Hand (Face down) */}
              <div className="flex -space-x-3">
                {Array.from({ length: Math.min(5, (battle.enemy_hand || []).length || 4) }).map((_, i) => (
                  <div
                    key={i}
                    className="w-8 h-12 bg-gradient-to-br from-[#2a1212] to-[#0a0505] border border-red-900/50 rounded shadow-md transform rotate-[-2deg] flex items-center justify-center font-heading text-[7px] text-red-500/30 font-bold"
                  >
                    AC
                  </div>
                ))}
              </div>

              {/* Opponent Gem */}
              <div className="flex items-center gap-1.5 bg-black/45 border border-red-500/35 rounded-full px-2.5 py-1">
                <div className="ccg-gem-energy ccg-gem-red">7</div>
                <span className="text-[9px] text-red-400 font-heading">MANA</span>
              </div>
            </div>

            {/* Opponent Minions & Boss Field Row */}
            <div className="ccg-slot-row overflow-x-auto no-scrollbar py-2">
              {(() => {
                const opponentSlots = Array.from({ length: 5 }).map((_, slotIdx) => {
                  if (slotIdx === 2) {
                    return { type: "boss", data: enemy };
                  }
                  const minionIdx = slotIdx < 2 ? slotIdx : slotIdx - 1;
                  const minion = (battle.enemy_field || [])[minionIdx];
                  if (minion && minion.hp > 0) {
                    return { type: "minion", data: minion };
                  }
                  return { type: "empty" };
                });

                return opponentSlots.map((slot, sIdx) => {
                  if (slot.type === "boss") {
                    return (
                      <div key={`boss-${sIdx}`} ref={(el) => registerRef("enemy-portrait", el)} className="relative">
                        <div
                          className={`relative ccg-card-slot ccg-card-slot-opponent flex flex-col justify-between shrink-0 overflow-hidden bg-gradient-to-b from-[#3a1010] via-[#160b0b] to-[#0a0303] border-2 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.35)] hover:scale-105 transition-all duration-300 ${
                            enemyHit ? "anim-enemy-hit anim-damage-flash" : ""
                          } ${lungeAnim === "enemy" ? "anim-enemy-lunge" : ""}`}
                        >
                          {/* Portrait */}
                          <div className="relative h-[46px] sm:h-[66px] lg:h-[80px] xl:h-[96px] bg-black/40 overflow-hidden rounded-t-[6px] border-b border-red-900/60 w-full shrink-0">
                            {enemy.image_url ? (
                              <img
                                src={resolveImageUrl(enemy.image_url, BACKEND_URL)}
                                alt={enemy.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Crown size={24} className="text-red-700/50" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent pointer-events-none" />
                            
                            {/* Boss phase & enrage indicators */}
                            {isBoss && battle?.boss_phase && (
                              <div className="absolute bottom-1 right-1 z-10 scale-75 origin-bottom-right">
                                <span className={`text-[8px] font-heading px-1.5 py-0.5 rounded-full ${
                                  battle.boss_phase === 3 ? "bg-red-500 text-white animate-pulse" :
                                  battle.boss_phase === 2 ? "bg-orange-500 text-white" :
                                  "bg-emerald-500 text-white"
                                }`}>
                                  F{battle.boss_phase}
                                </span>
                              </div>
                            )}
                            {battle?.enrage_triggered && (
                              <div className="absolute top-1 left-1 z-10 scale-75 origin-top-left animate-pulse">
                                <span className="text-[8px] font-heading px-1.5 py-0.5 rounded-full bg-red-600 text-white font-bold">
                                  FURIA
                                </span>
                              </div>
                            )}

                            <div className="absolute top-0.5 right-0.5 px-1 py-0.5 rounded bg-black/80 border border-[#cfb53b]/50 flex items-center gap-0.5 z-10">
                              <Crown size={8} className="text-[#cfb53b] shrink-0" />
                              <span className="text-[7px] font-heading text-[#cfb53b] uppercase">{isBoss ? "BOSS" : "NEMICO"}</span>
                            </div>
                          </div>

                          {/* Name */}
                          <div className="px-1 py-0.5 text-center shrink-0 w-full">
                            <p className="font-heading text-[9px] sm:text-[10px] lg:text-[11px] xl:text-xs text-white leading-tight truncate">
                              {enemy.name}
                            </p>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center justify-between px-1.5 py-0.5 text-[8px] sm:text-[9px] lg:text-[10px] xl:text-[11px] font-heading shrink-0 w-full bg-black/20">
                            <span className="flex items-center gap-0.5 text-red-400">
                              <Sword size={8} className="lg:w-2.5 lg:h-2.5" /> {enemy.attack ?? 0}
                            </span>
                            <span className="flex items-center gap-0.5 text-blue-400">
                              <Shield size={8} className="lg:w-2.5 lg:h-2.5" /> {enemy.defense ?? 0}
                            </span>
                          </div>

                          {/* HP Bar */}
                          <div className="px-1.5 py-0.5 shrink-0 w-full">
                            <div className="h-1 rounded-full bg-black/60 overflow-hidden border border-black/40">
                              <div
                                className={`h-full ${enemyHpColor === "high" ? "bg-emerald-500" : enemyHpColor === "medium" ? "bg-amber-500" : "bg-red-500"} transition-all duration-500`}
                                style={{ width: `${enemyHpPercent}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-center gap-0.5 mt-0.5 text-[7px] text-pink-200">
                              <Heart size={7} fill="currentColor" /> {enemy.hp}/{enemy.max_hp}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (slot.type === "minion") {
                    const minion = slot.data;
                    return (
                      <div key={`minion-${sIdx}`} className={`relative ccg-card-slot ccg-card-slot-opponent flex flex-col justify-between shrink-0 overflow-hidden bg-gradient-to-b from-[#2d1212] via-[#120808] to-[#070303] border border-red-800 shadow-md ${lungeAnim === "enemy" ? "anim-enemy-lunge" : ""} ${enemyHit ? "anim-enemy-hit anim-damage-flash" : ""}`}>
                        {/* Minion Portrait */}
                        <div className="relative h-[46px] sm:h-[66px] lg:h-[80px] xl:h-[96px] bg-black/40 overflow-hidden rounded-t-[6px] border-b border-red-900/60 w-full shrink-0">
                          {minion.image_url ? (
                            <img src={minion.image_url} alt={minion.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Users size={20} className="text-red-955" />
                            </div>
                          )}
                        </div>
                        {/* Name */}
                        <div className="px-1 py-0.5 text-center shrink-0 w-full">
                          <p className="font-heading text-[9px] sm:text-[10px] lg:text-[11px] xl:text-xs text-white leading-tight truncate">
                            {minion.name}
                          </p>
                        </div>
                        {/* Stats */}
                        <div className="flex items-center justify-between px-1.5 py-0.5 text-[8px] sm:text-[9px] lg:text-[10px] xl:text-[11px] font-heading shrink-0 w-full bg-black/20">
                          <span className="flex items-center gap-0.5 text-red-400">
                            <Sword size={8} className="lg:w-2.5 lg:h-2.5" /> {minion.attack ?? 0}
                          </span>
                          <span className="flex items-center gap-0.5 text-blue-400">
                            <Shield size={8} className="lg:w-2.5 lg:h-2.5" /> {minion.defense ?? 0}
                          </span>
                        </div>
                        {/* HP */}
                        <div className="px-1.5 py-0.5 shrink-0 w-full">
                          <div className="flex items-center justify-center gap-0.5 text-[7px] text-pink-200">
                            <Heart size={7} fill="currentColor" /> {minion.hp}/{minion.max_hp || minion.hp}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={`empty-${sIdx}`} className="ccg-card-slot ccg-card-slot-opponent shrink-0 opacity-20">
                      <Users size={16} className="text-red-800/20" />
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* 2. MIDDLE ZONE (DIVIDER & END TURN BUTTON) */}
          <div className="ccg-divider z-10">
            <div className="ccg-divider-medal" />

            {/* Hearthstone-style circular End Turn button */}
            <button
              data-testid="end-turn-button"
              onClick={handleEndTurn}
              disabled={acting || !isPlayerTurn}
              className={`ccg-end-turn-orb ${isPlayerTurn ? "ccg-end-turn-orb-player" : "ccg-end-turn-orb-enemy"}`}
            >
              {acting ? (
                <Loader2 className="animate-spin text-white" size={18} />
              ) : (
                <div className="ccg-end-turn-text">
                  <span>{isPlayerTurn ? (lang.startsWith("it") ? "FINE" : "END") : (lang.startsWith("it") ? "ATTESA" : "WAIT")}</span>
                  <span className="block text-[6px] opacity-80">{isPlayerTurn ? (lang.startsWith("it") ? "TURNO" : "TURN") : ""}</span>
                </div>
              )}
            </button>
          </div>

          {/* 3. PLAYER BOARD ROW */}
          <div className="w-full z-10">
            <div ref={(el) => registerRef("battlefield-zone", el)}>
              <BattleAlliedField
                allies={battle.player_field || []}
                lang={lang}
                registerRef={registerRef}
                enemies={{ main: battle.enemy, minions: battle.enemy_field || [] }}
                allyTargets={allyTargets}
                onPickTarget={handleAllyPickTarget}
                alliesAttacking={lungeAnim === "allies"}
                alliesHit={alliesHit}
              />
            </div>
          </div>

          {/* Action Animations Overlays */}
          {overlayAnim && (
            <div className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none">
              {overlayAnim === "slash-left" && (
                <div className="anim-slash-left">
                  <Swords size={64} className="text-red-500 drop-shadow-[0_0_20px_rgba(239,68,68,0.8)]" />
                </div>
              )}
              {overlayAnim === "slash-right" && (
                <div className="anim-slash-right">
                  <Swords size={64} className="text-[#8b0000] drop-shadow-[0_0_20px_rgba(139,0,0,0.8)]" />
                </div>
              )}
              {overlayAnim === "heal" && (
                <div className="anim-heal">
                  <Heart size={54} className="text-green-400 drop-shadow-[0_0_20px_rgba(74,222,128,0.9)]" />
                </div>
              )}
              {overlayAnim === "shield" && (
                <div className="anim-shield">
                  <Shield size={54} className="text-blue-400 drop-shadow-[0_0_20px_rgba(96,165,250,0.9)]" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* 4. PLAYER CONTROLS & HAND AREA */}
        {isPlayerTurn && (
          <div className="mt-3 flex flex-col md:flex-row gap-4 items-center justify-between z-20 relative">
            {/* Left HUD: Deck, HP/Mana, Statuses */}
            <div className="flex items-center gap-3 shrink-0 self-start md:self-center">
              <div className="ccg-deck-pile flex items-center justify-center font-heading text-[10px] text-[#cfb53b]/80 border-2 border-[#cfb53b]" title={lang.startsWith("it") ? "Mazzo" : "Deck"}>
                <span className="absolute -top-2.5 bg-black/80 px-1 rounded text-[7px] border border-[#cfb53b]/30">DECK</span>
                <span className="font-bold text-sm text-[#cfb53b]">{battle.player_deck?.length || 0}</span>
              </div>
              
              <div className="flex flex-col gap-1">
                <div
                  ref={(el) => registerRef("player-portrait", el)}
                  className={`flex items-center gap-2 bg-black/60 border border-[#cfb53b]/30 rounded-full px-3.5 py-1.5 relative ${
                    playerHit ? "anim-player-hit anim-damage-flash" : ""
                  }`}
                  data-testid="player-battle-hp"
                >
                  <div className="flex items-center gap-1">
                    <Heart size={14} fill="#ef4444" className="text-red-500 shrink-0" />
                    <span className="font-heading text-xs text-red-300 font-bold">{Math.max(0, character.hp)}</span>
                    <span className="text-[9px] text-red-500/50">/{character.max_hp}</span>
                  </div>
                  <span className="w-[1px] h-3 bg-slate-700/60" />
                  <div className="flex items-center gap-1">
                    <div className="ccg-gem-energy ccg-gem-blue text-[9px] w-5 h-5 shrink-0">{battle.player_mana}</div>
                    <span className="text-[8px] text-blue-400 font-heading">MANA</span>
                  </div>
                </div>

                {battle.player_temp_defense > 0 && (
                  <span className="text-[10px] text-blue-300 flex items-center gap-1 self-start ml-2 bg-blue-900/20 border border-blue-500/30 px-2 py-0.5 rounded-full">
                    <Shield size={10} /> {t(lang, 'armor')}: {battle.player_temp_defense}
                  </span>
                )}
              </div>
            </div>

            {/* Center Hand: Cards fan */}
            <div className="flex-1 w-full max-w-xl md:max-w-3xl lg:max-w-4xl xl:max-w-5xl">
              <BattleHand
                hand={(battle.player_hand || []).map((c) => translateCard(c, lang))}
                mana={battle.player_mana}
                acting={acting}
                onPlayCard={handlePlayCard}
                lang={lang}
                registerRef={registerRef}
                deckCount={(battle.player_deck || []).length}
              />
            </div>

            {/* Right HUD: Graveyard, Inventory & Flee buttons */}
            <div className="flex items-center gap-3 shrink-0 self-end md:self-center">
              <div className="ccg-grave-pile flex items-center justify-center border-2 border-slate-700" title={lang.startsWith("it") ? "Cimitero" : "Graveyard"}>
                <span className="absolute -top-2.5 bg-black/80 px-1 rounded text-[7px] border border-slate-700/50">GRAVE</span>
                <Skull size={18} className="text-slate-500/50" />
              </div>

              <div className="flex flex-col gap-1.5">
                <Button
                  data-testid="battle-inventory-btn"
                  onClick={() => setShowBattleInv(!showBattleInv)}
                  variant="outline"
                  className="border-[#cfb53b]/30 text-[#cfb53b] hover:bg-[#cfb53b]/10 text-[10px] h-7 px-3 py-1 font-heading"
                >
                  <Backpack size={11} className="mr-1" />
                  {t(lang, 'battle_inventory')}
                </Button>
                {!enemy.is_boss && (
                  <Button
                    data-testid="flee-active-btn"
                    onClick={handleFleeActive}
                    disabled={acting || fleeingActive}
                    variant="outline"
                    className={`text-[10px] h-7 px-3 py-1 font-heading transition-all ${
                      fleeConfirm
                        ? "bg-red-500/20 text-red-300 border-red-500/50 hover:bg-red-500/30 animate-pulse"
                        : "border-[#8b0000]/40 text-[#cfb53b] hover:bg-[#8b0000]/10 hover:border-[#8b0000]/70"
                    }`}
                  >
                    <Footprints size={11} className="mr-1" />
                    {fleeConfirm ? (lang.startsWith("it") ? "CONFERMI?" : "SURE?") : t(lang, 'flee_battle')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Player statuses list */}
        {battle?.player_statuses && Object.entries(battle.player_statuses).some(([_, v]) => v > 0) && (
          <div className="mt-2 flex flex-wrap gap-1.5 justify-center" data-testid="player-statuses">
            {Object.entries(battle.player_statuses).map(([kind, turns]) => {
              if (!turns || turns <= 0) return null;
              const meta = {
                stunned:  { icon: "💫", label: "Stordito",  color: "text-purple-300 border-purple-500/40 bg-purple-500/10" },
                poisoned: { icon: "☠️", label: "Veleno",    color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
                cursed:   { icon: "🌑", label: "Maledetto", color: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
              }[kind] || { icon: "✨", label: kind, color: "text-[#94a3b8] border-[#475569]" };
              return (
                <span
                  key={kind}
                  className={`inline-flex items-center gap-1 text-[9px] font-heading uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.color}`}
                  data-testid={`status-${kind}`}
                >
                  <span>{meta.icon}</span>
                  <span>{meta.label}</span>
                  <span className="text-[8px] opacity-70">×{turns}</span>
                </span>
              );
            })}
          </div>
        )}

        {/* Battle Inventory Panel */}
        {showBattleInv && (
          <div className="mt-3 bg-[#1a1a1d]/90 border border-[#cfb53b]/25 rounded-lg p-3 animate-fadeInUp z-20 relative" data-testid="battle-inventory-panel">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading text-xs sm:text-sm text-[#cfb53b] flex items-center gap-1.5">
                <Backpack size={13} /> {t(lang, 'battle_inventory')}
              </h4>
              <Button variant="ghost" size="sm" onClick={() => setShowBattleInv(false)} className="h-6 w-6 p-0 text-[#94a3b8] hover:text-[#e2e8f0]">
                <X size={14} />
              </Button>
            </div>
            {(() => {
              const usableItems = [
                ...(game?.inventory?.abilities || []).filter(c => c.effect && c.effect !== "none" && !c.is_basic),
                ...(game?.inventory?.objects || []).filter(c => c.effect && c.effect !== "none" && !c.equippable),
              ];
              if (usableItems.length === 0) {
                return <p className="text-xs text-[#64748b] text-center py-4">{t(lang, 'no_objects')}</p>;
              }
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                  {usableItems.map(card => (
                    <GameCard
                      key={card.id}
                      card={card}
                      type={card.type || "ability"}
                      inBattle={true}
                      onUse={handleItemUsed}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Battle Log HUD Console */}
        <div className="bg-[#120f0d]/90 border border-[#2d231b] rounded-lg mt-4 shadow-lg overflow-hidden" data-testid="battle-log">
          <div className="px-3 py-1.5 border-b border-[#2d231b] bg-black/35 flex items-center justify-between">
            <span className="text-[10px] font-heading text-[#94a3b8] uppercase tracking-wider flex items-center gap-1.5">
              <Swords size={11} className="text-[#cfb53b]" /> {lang.startsWith("it") ? "Cronaca Battaglia" : "Battle Log"}
            </span>
            <span className="text-[9px] text-[#64748b] font-heading">
              {lang.startsWith("it") ? "Turno" : "Turn"} {battle.turn}
            </span>
          </div>
          <ScrollArea className="h-28 sm:h-36 px-3 py-2">
            <div className="flex flex-col gap-1.5">
              {classifiedLog.map((entry, i) => {
                const { text, kind } = entry;
                let rowClass = "text-xs leading-snug flex items-start gap-1.5 rounded px-2 py-1";
                let Icon = null;
                let iconColor = "";
                let align = "";
                if (kind === "combo") {
                  rowClass += " bg-[#cfb53b]/10 text-[#cfb53b] font-heading font-bold border-l-2 border-[#cfb53b]";
                  Icon = Zap; iconColor = "text-[#cfb53b]";
                } else if (kind === "enemy") {
                  rowClass += " bg-[#7a1f1f]/15 text-red-300 border-l-2 border-[#8b0000]";
                  Icon = Ghost; iconColor = "text-red-400";
                  align = "ml-auto text-right flex-row-reverse max-w-[85%]";
                } else if (kind === "player") {
                  rowClass += " bg-emerald-500/10 text-emerald-200 border-l-2 border-emerald-500/60";
                  Icon = User; iconColor = "text-emerald-400";
                  align = "max-w-[85%]";
                } else if (kind === "outcome") {
                  rowClass += " bg-purple-500/10 text-purple-200 font-heading";
                  Icon = Flame; iconColor = "text-purple-400";
                } else if (kind === "reward") {
                  rowClass += " bg-blue-500/10 text-blue-200";
                  Icon = Sparkles; iconColor = "text-blue-400";
                } else {
                  rowClass += " text-[#94a3b8]";
                }
                return (
                  <div key={i} className={`${rowClass} ${align}`.trim()}>
                    {Icon && <Icon size={10} className={`shrink-0 mt-0.5 ${iconColor}`} />}
                    <span className="break-words">{text}</span>
                  </div>
                );
              })}
              <div ref={logEndRef} />
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Cardflight flight animations project overlay */}
      <FlyOverlay />

      {/* Target choice picker for healing cards */}
      {healPickerCardId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" data-testid="heal-target-picker">
          <div className="bg-[#1a1a1d] border border-[#cfb53b]/50 rounded-lg p-5 max-w-sm w-full animate-fadeInUp">
            <h3 className="font-heading text-base text-[#cfb53b] mb-3 text-center">Chi vuoi curare?</h3>
            <div className="space-y-2">
              <button
                onClick={() => { const id = healPickerCardId; setHealPickerCardId(null); handlePlayCard(id, "player"); }}
                data-testid="heal-target-player"
                className="w-full text-left p-3 rounded border border-slate-700 hover:border-[#cfb53b] hover:bg-[#cfb53b]/10 text-xs text-[#e2e8f0] flex items-center justify-between"
              >
                <span className="flex items-center gap-2">
                  <span className="text-emerald-400">❤️</span>
                  <span>Te stesso</span>
                </span>
                <span className="text-[#94a3b8]">({game?.character?.hp}/{game?.character?.max_hp} HP)</span>
              </button>
              {(battle?.player_field || []).map((ally) => (
                <button
                  key={ally.id}
                  onClick={() => { const id = healPickerCardId; setHealPickerCardId(null); handlePlayCard(id, ally.id); }}
                  data-testid={`heal-target-${ally.id}`}
                  className="w-full text-left p-3 rounded border border-slate-700 hover:border-emerald-400 hover:bg-emerald-500/10 text-xs text-[#e2e8f0] flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-emerald-400">🛡️</span>
                    <span>{ally.name}</span>
                  </span>
                  <span className="text-[#94a3b8]">({ally.hp}/{ally.max_hp || ally.hp} HP)</span>
                </button>
              ))}
              <button
                onClick={() => setHealPickerCardId(null)}
                data-testid="heal-target-cancel"
                className="w-full mt-2 p-2 rounded text-xs text-[#94a3b8] hover:text-white text-center font-heading"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
